import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  formatCurrency,
  formatDuration,
} from "../../../../library/utils/numberFormatting";
import { Badge, Tooltip, Button, Checkbox } from "@fluentui/react-components";
import {
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  WarningRegular,
  InfoRegular,
  EditRegular,
} from "@fluentui/react-icons";
import EditPremiumDialog from "../../../../components/EditPremiumDialog/EditPremiumDialog";
import type {
  QuoteUpdateDto,
  ExcludedItemDto,
} from "../../../../library/types/types";

// Types
interface MtplPremium {
  Year: number;
  Payments: number;
  ClientPremium: number;
  InsurerPremium: number;
  InsurerDiscount: number;
  CommissionDiscount: number;
  DiscountDescription: string | null;
  Period: number; // 1, 3, 6, 9, 12 months
  Id: string | null;
  ConvenienceFee: number | null;
  QuoteId: string | null;
}

interface MtplError {
  Code: number;
  Message: string;
  Description: string | null;
}

interface MtplQuoteData {
  HasPrices: boolean;
  BonusMalus: string | null;
  Premiums?: MtplPremium[];
  Insurer: string;
  Calculator: string;
  ErrorList: MtplError[];
  Duration: number;
  InsurerRequest: string[];
  InsurerResponse: string[];
  HasErrors: boolean;
  HasInsurerMessages: boolean;
  HasInsurerWarnings: boolean;
  HasInsurerErrors: boolean;
  HasGenericErrors: boolean;
  HasValidationErrors: boolean;
  PolicyNumber?: string | null;
  PremiumUnderwritten?: number;
  HasOffer?: boolean;
  PolicyPeriod?: number | null;
  isSaveAttempt?: boolean; // Flag to distinguish save attempt from create offer
}

interface MtplQuotesDisplayProps {
  quotes: MtplQuoteData[];
  /** Save attempt quotes (MtplSaved from backend) */
  savedQuotes?: MtplQuoteData[];
  /** Originally requested insurers from the calculation request - all will be displayed as columns */
  requestedInsurers?: string[];
  /** Originally requested periods (months) from the calculation request - all will be displayed as rows */
  requestedPeriods?: number[];
  invoiceData?: {
    Insurer: string;
    Period: number;
    Payments: number;
  } | null;
  onSelectionChange?: (
    selection: {
      insurer: string;
      premium: number;
      payments: number;
      period?: number;
      insurerDiscount?: number;
      commissionDiscount?: number;
    } | null,
  ) => void;
  /** Updated quotes from backend (MtplUpdated) */
  updatedQuotes?: QuoteUpdateDto[];
  /** Callback when user edits a quote value */
  onQuoteUpdate?: (
    update: QuoteUpdateDto,
    originalPremium: number | null,
  ) => void;
  /** Excluded insurers for invoice generation */
  excludedItems?: ExcludedItemDto[];
  /** Callback when exclusions change */
  onExclusionChange?: (exclusions: ExcludedItemDto[]) => void;
  /** Whether editing is allowed (disabled after invoice/save) */
  canEdit?: boolean;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 99.9%;
  height: 100%;
  gap: 6px;
`;

const TableContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: auto;
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  width: 100%;
  height: 100%;
  margin-bottom: 16px;

  /* Remove inner table borders that overlap with container */
  table {
    th:first-child,
    td:first-child {
      border-left: none;
    }

    thead tr:first-child th {
      border-top: none;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: max-content;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
`;

const TableHeader = styled.thead`
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};

  th {
    padding: 8px 4px;
    font-weight: 600;
    font-size: 11px;
    color: ${({ theme }) => theme.semanticColors.bodySubtext};
    border-bottom: 2px solid
      ${({ theme }) => theme.semanticColors.bodyFrameDivider};
    border-right: 1px solid
      ${({ theme }) => theme.semanticColors.bodyFrameDivider};
    border-top: 1px solid
      ${({ theme }) => theme.semanticColors.bodyFrameDivider};
    text-align: center;
    vertical-align: top;
    min-width: 85px;
    max-width: 120px;

    &:first-child {
      width: 140px;
      min-width: 140px;
      text-align: left;
      position: sticky;
      left: 0;
      background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
      z-index: 2;
      border-left: 1px solid
        ${({ theme }) => theme.semanticColors.bodyFrameDivider};
    }
  }
`;

const InsurerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const StatusIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
`;

const StatusIconWrapper = styled.div<{ tooltip?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InsurerName = styled.div`
  font-size: 12px;
  font-weight: 600;
  word-break: break-word;
  text-align: center;
`;

const TableBody = styled.tbody`
  tr:nth-child(even) {
    background: ${({ theme }) =>
      theme.isDark
        ? theme.semanticColors.bodyBackground
        : theme.semanticColors.bodyStandoutBackground};
  }

  tr:hover {
    background: ${({ theme }) => theme.semanticColors.bodyBackgroundHovered};
  }
`;

const TableRow = styled.tr<{ isSection?: boolean; hasError?: boolean }>`
  ${({ isSection, theme }) =>
    isSection &&
    `
    background: ${
      theme.isDark ? theme.semanticColors.bodyBackgroundHovered : "#f0f0f0"
    } !important;
    font-weight: 600;
  `}

  ${({ hasError, theme }) =>
    hasError &&
    `
    background: ${
      theme.isDark ? "rgba(255, 77, 79, 0.15)" : "#fff2f0"
    } !important;
  `}

  td {
    padding: 6px 4px;
    border-bottom: 1px solid ${({ theme }) => theme.semanticColors.bodyDivider};
    border-right: 1px solid ${({ theme }) => theme.semanticColors.bodyDivider};
    text-align: center;
    vertical-align: middle;
    font-size: 11px;
    color: ${({ theme }) => theme.semanticColors.bodyText};
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:first-child {
      text-align: left;
      font-weight: 600;
      position: sticky;
      left: 0;
      background: inherit;
      z-index: 1;
      padding-left: 12px;
      width: 140px;
      min-width: 140px;
      white-space: normal;
      border-left: 1px solid ${({ theme }) => theme.semanticColors.bodyDivider};
    }

    &.selected-premium {
      background: ${({ theme }) =>
        theme.isDark ? "rgba(0, 120, 212, 0.2)" : "#e6f7ff"} !important;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        border: 2px solid
          ${({ theme }) => (theme.isDark ? "#4db8ff" : "#1890ff")};
        pointer-events: none;
      }
    }
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const RowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  gap: 2px;
  font-size: 11px;

  input[type="radio"] {
    cursor: pointer;
    margin: 0;
    width: 14px;
    height: 14px;
  }
`;

const PremiumValue = styled.div`
  font-size: 11px;
`;

const PremiumCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  position: relative;

  &:hover .edit-icon-button {
    opacity: 1;
  }
`;

const EditIconButton = styled(Button)`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 20px;
  height: 20px;
  padding: 2px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;

  &.always-visible {
    opacity: 1;
  }
`;

const UpdatedValue = styled.span`
  color: ${({ theme }) => (theme.isDark ? "#ff7875" : "#ff4d4f")};
  font-weight: 600;
`;

const PolicyNumberInput = styled.input`
  width: 100%;
  max-width: 110px;
  padding: 2px 4px;
  font-size: 9px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyDivider};
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  color: ${({ theme }) => theme.semanticColors.bodyText};
  border-radius: 2px;
  cursor: default;

  &:focus {
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  font-size: 11px;
  color: ${({ theme }) => (theme.isDark ? "#ff7875" : "#ff4d4f")};
  max-width: 150px;
  margin: 0 auto;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const WarningMessage = styled.div`
  font-size: 11px;
  color: ${({ theme }) => (theme.isDark ? "#ffc53d" : "#faad14")};
  cursor: help;
  max-width: 150px;
  margin: 0 auto;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const NoDataCell = styled.span`
  color: ${({ theme }) => theme.semanticColors.disabledText};
`;

const ExcludedCell = styled.td<{ excluded?: boolean }>`
  ${({ excluded, theme }) =>
    excluded &&
    `
    opacity: 0.4;
    background-color: ${theme.semanticColors.bodyStandoutBackground} !important;
    
    * {
      text-decoration: line-through;
      color: ${theme.semanticColors.bodySubtext} !important;
    }
  `}
`;

const ExcludedHeader = styled.th<{ excluded?: boolean }>`
  ${({ excluded, theme }) =>
    excluded &&
    `
    opacity: 0.5;
    background-color: ${theme.semanticColors.bodyStandoutBackground} !important;
    
    * {
      color: ${theme.semanticColors.bodySubtext} !important;
    }
  `}
`;

const InsurerCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;

  /* Make checkbox more subtle with muted teal */
  .fui-Checkbox {
    --colorCompoundBrandBackground: #4a9a9a;
    --colorCompoundBrandBackgroundHover: #3d8585;
    --colorCompoundBrandBackgroundPressed: #357575;
  }

  .fui-Checkbox__indicator {
    border-color: #7ab5b5;
  }

  .fui-Checkbox:checked .fui-Checkbox__indicator {
    background-color: #4a9a9a;
    border-color: #4a9a9a;
  }
`;

const NoQuotesMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};

  svg {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

const MtplQuotesDisplay: React.FC<MtplQuotesDisplayProps> = ({
  quotes,
  savedQuotes = [],
  requestedInsurers,
  requestedPeriods,
  invoiceData,
  onSelectionChange,
  updatedQuotes = [],
  onQuoteUpdate,
  excludedItems = [],
  onExclusionChange,
  canEdit = true,
}) => {
  const { t } = useTranslation();

  // Helper to check if an insurer is excluded
  const isInsurerExcluded = (insurer: string): boolean => {
    return excludedItems.some(
      (item) =>
        item.Insurer.toLowerCase() === insurer.toLowerCase() &&
        (item.VariantID === undefined || item.VariantID === null),
    );
  };

  // Handler to toggle insurer exclusion (excludes all variants when no VariantID)
  const handleInsurerExclusionToggle = (insurer: string) => {
    if (!onExclusionChange) return;

    const isCurrentlyExcluded = isInsurerExcluded(insurer);

    if (isCurrentlyExcluded) {
      // Remove insurer exclusion
      const newExcluded = excludedItems.filter(
        (item) =>
          !(
            item.Insurer.toLowerCase() === insurer.toLowerCase() &&
            (item.VariantID === undefined || item.VariantID === null)
          ),
      );
      onExclusionChange(newExcluded);
    } else {
      // Add insurer exclusion (with VariantID: null for consistency with backend format)
      const newExcluded = [
        ...excludedItems,
        { Insurer: insurer, VariantID: null },
      ];
      onExclusionChange(newExcluded);
    }
  };

  // Edit dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState<{
    insurer: string;
    period: number;
    payments: number;
    variantID: number | null;
    originalPremium: number | null;
    currentPremium: number | null;
  } | null>(null);

  // Group quotes by insurer and merge saved policies with regular premiums
  const { groupedQuotes, savedPoliciesByPeriod, allSavedPoliciesByInsurer } =
    React.useMemo(() => {
      const grouped: { [insurer: string]: MtplQuoteData } = {};
      const savedByPeriod: {
        [period: number]: { [insurer: string]: MtplQuoteData };
      } = {};
      const allSavedByInsurer: { [insurer: string]: MtplQuoteData[] } = {};

      quotes.forEach((quote) => {
        const insurerKey = quote.Insurer.toLowerCase();

        // Track ALL saved policy attempts for this insurer (for error display)
        if (quote.HasOffer) {
          if (!allSavedByInsurer[insurerKey]) {
            allSavedByInsurer[insurerKey] = [];
          }
          allSavedByInsurer[insurerKey]!.push(quote);
        }

        // Handle saved policies with PolicyPeriod and PremiumUnderwritten (non-zero)
        if (
          quote.HasOffer &&
          quote.PolicyPeriod !== null &&
          quote.PolicyPeriod !== undefined &&
          quote.PremiumUnderwritten !== undefined &&
          quote.PremiumUnderwritten !== 0
        ) {
          const period = quote.PolicyPeriod;
          if (!savedByPeriod[period]) {
            savedByPeriod[period] = {};
          }
          savedByPeriod[period]![insurerKey] = quote;
        }

        if (!grouped[insurerKey]) {
          // First entry for this insurer - use it as base
          grouped[insurerKey] = { ...quote };
        } else {
          // Merge premiums from duplicate entries
          const existing = grouped[insurerKey];

          // Merge Premiums arrays
          if (quote.Premiums && quote.Premiums.length > 0) {
            const existingPremiums = existing.Premiums || [];
            const mergedPremiums = [...existingPremiums];

            quote.Premiums.forEach((newPremium) => {
              // Only add if this period doesn't already exist
              const existingPremiumIndex = mergedPremiums.findIndex(
                (p) => p.Period === newPremium.Period,
              );
              if (existingPremiumIndex === -1) {
                mergedPremiums.push(newPremium);
              }
            });

            existing.Premiums = mergedPremiums;
          }

          // Merge error lists
          if (quote.ErrorList && quote.ErrorList.length > 0) {
            const existingErrors = existing.ErrorList || [];
            const errorMessages = new Set(existingErrors.map((e) => e.Message));

            quote.ErrorList.forEach((error) => {
              if (!errorMessages.has(error.Message)) {
                existingErrors.push(error);
                errorMessages.add(error.Message);
              }
            });

            existing.ErrorList = existingErrors;
          }

          // Update flags
          existing.HasPrices = existing.HasPrices || quote.HasPrices;
          existing.HasErrors = existing.HasErrors || quote.HasErrors;
          existing.HasInsurerMessages =
            existing.HasInsurerMessages || quote.HasInsurerMessages;
          existing.HasInsurerWarnings =
            existing.HasInsurerWarnings || quote.HasInsurerWarnings;
          existing.HasInsurerErrors =
            existing.HasInsurerErrors || quote.HasInsurerErrors;
          existing.HasGenericErrors =
            existing.HasGenericErrors || quote.HasGenericErrors;
          existing.HasValidationErrors =
            existing.HasValidationErrors || quote.HasValidationErrors;

          // Keep BonusMalus if found
          if (quote.BonusMalus && !existing.BonusMalus) {
            existing.BonusMalus = quote.BonusMalus;
          }

          // Keep PolicyNumber if found (prefer non-null values)
          if (quote.PolicyNumber && !existing.PolicyNumber) {
            existing.PolicyNumber = quote.PolicyNumber;
          }

          // Keep PremiumUnderwritten if found
          if (quote.PremiumUnderwritten && !existing.PremiumUnderwritten) {
            existing.PremiumUnderwritten = quote.PremiumUnderwritten;
          }

          // Keep HasOffer flag
          if (quote.HasOffer) {
            existing.HasOffer = quote.HasOffer;
          }

          // Keep PolicyPeriod if found
          if (quote.PolicyPeriod && !existing.PolicyPeriod) {
            existing.PolicyPeriod = quote.PolicyPeriod;
          }
        }
      });

      // Also add saved quotes from the savedQuotes prop (Response.MtplSaved)
      savedQuotes.forEach((saved) => {
        const insurerKey = saved.Insurer.toLowerCase();
        if (!allSavedByInsurer[insurerKey]) {
          allSavedByInsurer[insurerKey] = [];
        }
        allSavedByInsurer[insurerKey]!.push(saved);
      });

      return {
        groupedQuotes: Object.values(grouped),
        savedPoliciesByPeriod: savedByPeriod,
        allSavedPoliciesByInsurer: allSavedByInsurer,
      };
    }, [quotes, savedQuotes]);

  // State for single selected premium (format: "InsurerName-Period")
  const [selectedPremium, setSelectedPremium] = useState<string | null>(null);

  // Track if we've made the initial selection to avoid re-selecting on every render
  const hasInitializedRef = useRef(false);
  // Track the previous invoiceData to detect when invoice is issued
  const prevInvoiceDataRef = useRef(invoiceData);

  // Check if a specific radio option matches the invoice
  const isInvoicedOption = (insurer: string, period: number): boolean => {
    if (!invoiceData) return false;
    return (
      invoiceData.Insurer.toLowerCase() === insurer.toLowerCase() &&
      invoiceData.Period === period
    );
  };

  // Find the invoiced premium if invoice exists
  const invoicedPremiumKey = useMemo(() => {
    if (!invoiceData) return null;

    // The invoice identifies the selection (insurer + period), whether the premium came from
    // the insurer, a saved policy or a manual update that didn't exist in the original offer
    const matchingQuote = groupedQuotes.find(
      (q) => q.Insurer.toLowerCase() === invoiceData.Insurer.toLowerCase(),
    );
    // Use the quote's insurer name casing to match the radio keys
    const selectedKey = matchingQuote
      ? `${matchingQuote.Insurer}-${invoiceData.Period}`
      : null;

    console.log(
      "MTPL invoicedPremiumKey:",
      selectedKey,
      "invoiceData:",
      invoiceData,
    );
    return selectedKey;
  }, [groupedQuotes, invoiceData]);

  // Calculate the premium with lowest price for 12-month period
  const lowestPremiumKey = useMemo(() => {
    let lowestPremium = Infinity;
    let selectedKey: string | null = null;

    groupedQuotes.forEach((quote) => {
      if (quote.HasPrices && quote.Premiums && quote.Premiums.length > 0) {
        // Prefer 12-month period
        const yearlyPremium = quote.Premiums.find((p) => p.Period === 12);
        if (yearlyPremium && yearlyPremium.ClientPremium < lowestPremium) {
          lowestPremium = yearlyPremium.ClientPremium;
          selectedKey = `${quote.Insurer}-12`;
        }
      }
    });

    return selectedKey;
  }, [groupedQuotes]);

  // Set initial selection only once when component mounts or quotes first become available
  // Prioritize invoiced premium if invoice exists
  useEffect(() => {
    // Always update selection when invoiceData changes (invoice is issued)
    // This ensures the invoiced variant is selected even if we already initialized
    const invoiceDataChanged = prevInvoiceDataRef.current !== invoiceData;
    prevInvoiceDataRef.current = invoiceData;

    console.log("MTPL Selection useEffect:", {
      invoiceDataChanged,
      invoicedPremiumKey,
      lowestPremiumKey,
      currentSelection: selectedPremium,
      hasInitialized: hasInitializedRef.current,
    });

    if (invoiceDataChanged && invoicedPremiumKey) {
      // Invoice was just issued - force select the invoiced premium
      console.log("Setting selection to invoiced premium:", invoicedPremiumKey);
      setSelectedPremium(invoicedPremiumKey);
      return;
    }

    if (!hasInitializedRef.current) {
      if (invoicedPremiumKey) {
        console.log(
          "Initial selection - invoiced premium:",
          invoicedPremiumKey,
        );
        setSelectedPremium(invoicedPremiumKey);
        hasInitializedRef.current = true;
      } else if (lowestPremiumKey) {
        console.log("Initial selection - lowest premium:", lowestPremiumKey);
        setSelectedPremium(lowestPremiumKey);
        hasInitializedRef.current = true;
      }
    }
  }, [lowestPremiumKey, invoicedPremiumKey, invoiceData]);

  const getCreateOfferStatusIcon = (quote: MtplQuoteData | null) => {
    if (!quote) {
      return <InfoRegular style={{ fontSize: 14, color: "#8c8c8c" }} />;
    }
    if (quote.HasErrors) {
      return <ErrorCircleRegular style={{ fontSize: 14, color: "#ff4d4f" }} />;
    }
    if (quote.HasInsurerMessages || quote.HasInsurerWarnings) {
      return <WarningRegular style={{ fontSize: 14, color: "#faad14" }} />;
    }
    if (quote.HasPrices && quote.Premiums && quote.Premiums.length > 0) {
      return (
        <CheckmarkCircleRegular style={{ fontSize: 14, color: "#52c41a" }} />
      );
    }
    return <div style={{ width: 14, height: 14 }} />;
  };

  const getSaveOfferStatusIcon = (savedQuote: MtplQuoteData | null) => {
    if (!savedQuote) {
      return <InfoRegular style={{ fontSize: 14, color: "#d9d9d9" }} />;
    }
    if (savedQuote.HasErrors || savedQuote.HasInsurerErrors) {
      return <ErrorCircleRegular style={{ fontSize: 14, color: "#ff4d4f" }} />;
    }
    if (savedQuote.PolicyNumber || savedQuote.HasOffer) {
      return (
        <CheckmarkCircleRegular style={{ fontSize: 14, color: "#52c41a" }} />
      );
    }
    if (savedQuote.HasInsurerWarnings) {
      return <WarningRegular style={{ fontSize: 14, color: "#faad14" }} />;
    }
    return <InfoRegular style={{ fontSize: 14, color: "#d9d9d9" }} />;
  };

  // Using centralized formatDuration from numberFormatting utils

  // Get all unique periods across all quotes AND requested periods
  const getAllPeriods = (): number[] => {
    const periodSet = new Set<number>();
    // Include originally requested periods first (ensure they're all displayed)
    if (requestedPeriods && requestedPeriods.length > 0) {
      requestedPeriods.forEach((p) => periodSet.add(p));
    }
    // Include periods from regular premiums
    groupedQuotes.forEach((quote) => {
      if (quote.Premiums) {
        quote.Premiums.forEach((p) => periodSet.add(p.Period));
      }
    });
    // Include periods from saved policies
    Object.keys(savedPoliciesByPeriod).forEach((period) => {
      periodSet.add(Number(period));
    });
    return Array.from(periodSet).sort((a, b) => a - b);
  };

  // Build complete list of insurers to display (requested + returned quotes + saved quotes)
  const allInsurersToDisplay = useMemo(() => {
    const insurerMap: {
      [key: string]: {
        name: string;
        quote: MtplQuoteData | null;
        savedQuote: MtplQuoteData | null;
      };
    } = {};

    // First, add all requested insurers (these should always be displayed)
    if (requestedInsurers && requestedInsurers.length > 0) {
      requestedInsurers.forEach((insurer) => {
        const key = insurer.toLowerCase();
        insurerMap[key] = { name: insurer, quote: null, savedQuote: null };
      });
    }

    // Then, add/update with actual quotes data
    groupedQuotes.forEach((quote) => {
      const key = quote.Insurer.toLowerCase();
      if (!insurerMap[key]) {
        insurerMap[key] = { name: quote.Insurer, quote, savedQuote: null };
      } else {
        insurerMap[key].quote = quote;
        insurerMap[key].name = quote.Insurer; // Use the quote's insurer name
      }
    });

    // Add saved quotes data
    savedQuotes.forEach((savedQuote) => {
      const key = savedQuote.Insurer.toLowerCase();
      if (!insurerMap[key]) {
        insurerMap[key] = { name: savedQuote.Insurer, quote: null, savedQuote };
      } else {
        insurerMap[key].savedQuote = savedQuote;
      }
    });

    // Convert to array sorted by insurer name
    return Object.values(insurerMap).sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    );
  }, [groupedQuotes, requestedInsurers, savedQuotes]);

  // Helper: Find updated quote value for a specific insurer/period combination
  const getUpdatedQuote = useCallback(
    (insurer: string, period: number): QuoteUpdateDto | undefined => {
      return updatedQuotes.find(
        (uq) =>
          uq.Insurer.toLowerCase() === insurer.toLowerCase() &&
          uq.Period === period,
      );
    },
    [updatedQuotes],
  );

  // Get premium for period - prioritize updated, then saved policy, then regular premium
  const getPremiumDataForPeriod = (
    quote: MtplQuoteData,
    period: number,
  ): {
    premium: number | null;
    policyNumber?: string | null;
    isSaved: boolean;
    isUpdated: boolean;
    originalPremium?: number | null;
  } | null => {
    const insurerKey = quote.Insurer.toLowerCase();

    // Check for updated quote first
    const updatedQuote = getUpdatedQuote(quote.Insurer, period);

    // Check if there's a saved policy for this period/insurer
    const savedPolicy = savedPoliciesByPeriod[period]?.[insurerKey];

    let originalPremium: number | null = null;

    // Determine original premium (saved or regular)
    if (
      savedPolicy?.PremiumUnderwritten !== undefined &&
      savedPolicy.PremiumUnderwritten !== 0
    ) {
      originalPremium = savedPolicy.PremiumUnderwritten;
    } else if (quote.Premiums) {
      const regularPremium = quote.Premiums.find((p) => p.Period === period);
      if (regularPremium) {
        originalPremium = regularPremium.ClientPremium;
      }
    }

    // If updated quote exists, use it
    if (updatedQuote) {
      return {
        premium: updatedQuote.Premium,
        policyNumber: savedPolicy?.PolicyNumber,
        isSaved: savedPolicy !== undefined,
        isUpdated: true,
        originalPremium,
      };
    }

    // Otherwise use saved or regular premium
    if (
      savedPolicy?.PremiumUnderwritten !== undefined &&
      savedPolicy.PremiumUnderwritten !== 0
    ) {
      return {
        premium: savedPolicy.PremiumUnderwritten,
        policyNumber: savedPolicy.PolicyNumber,
        isSaved: true,
        isUpdated: false,
        originalPremium: savedPolicy.PremiumUnderwritten,
      };
    }

    // Fallback to regular premium
    if (!quote.Premiums) return null;
    const regularPremium = quote.Premiums.find((p) => p.Period === period);
    if (regularPremium) {
      return {
        premium: regularPremium.ClientPremium,
        policyNumber: null,
        isSaved: false,
        isUpdated: false,
        originalPremium: regularPremium.ClientPremium,
      };
    }

    return null;
  };

  // Handle edit button click
  const handleEditClick = (
    insurer: string,
    period: number,
    premiumData: ReturnType<typeof getPremiumDataForPeriod>,
  ) => {
    if (!canEdit || !onQuoteUpdate || !premiumData) return;

    // Find the payments value for this quote
    const quote = groupedQuotes.find(
      (q) => q.Insurer.toLowerCase() === insurer.toLowerCase(),
    );
    const premium = quote?.Premiums?.find((p) => p.Period === period);
    const payments = premium?.Payments || 1;

    setEditingQuote({
      insurer,
      period,
      payments,
      variantID: null, // MTPL doesn't have variants
      originalPremium: premiumData.originalPremium ?? null,
      currentPremium: premiumData.premium,
    });
    setIsEditDialogOpen(true);
  };

  // Handle save from edit dialog
  const handleSaveEdit = (update: QuoteUpdateDto) => {
    if (onQuoteUpdate && editingQuote) {
      onQuoteUpdate(update, editingQuote.originalPremium);
    }
    setIsEditDialogOpen(false);
    setEditingQuote(null);
  };

  // Get unique error messages (deduplication)
  const getUniqueErrors = (errors: MtplError[]): MtplError[] => {
    const seen = new Set<string>();
    return errors.filter((error) => {
      if (seen.has(error.Message)) return false;
      seen.add(error.Message);
      return true;
    });
  };

  const handlePremiumSelection = useCallback(
    (insurer: string, period: number) => {
      setSelectedPremium(`${insurer}-${period}`);

      // Find the selected quote and premium to notify parent
      if (onSelectionChange) {
        const quote = quotes.find((q) => q.Insurer === insurer);
        if (quote) {
          // Use getPremiumDataForPeriod to get the correct premium (updated if exists, or original)
          const premiumData = getPremiumDataForPeriod(quote, period);

          if (premiumData && premiumData.premium !== null) {
            // Find the original premium data for discount info
            const originalPremiumData = quote.Premiums?.find(
              (p) => p.Period === period,
            );

            onSelectionChange({
              insurer,
              premium: premiumData.premium, // Use updated premium if available
              payments: originalPremiumData?.Payments || 1,
              period: period,
              insurerDiscount: originalPremiumData?.InsurerDiscount || 0,
              commissionDiscount: originalPremiumData?.CommissionDiscount || 0,
            });
          } else {
            onSelectionChange(null);
          }
        } else {
          onSelectionChange(null);
        }
      }
    },
    [quotes, savedPoliciesByPeriod],
    // eslint-disable-next-line react-hooks/exhaustive-deps
  );

  // Notify parent of initial selection
  useEffect(() => {
    if (selectedPremium) {
      const parts = selectedPremium.split("-");
      if (parts.length === 2 && parts[0] && parts[1]) {
        const insurer = parts[0];
        const period = parseInt(parts[1], 10);

        // Trigger selection notification
        handlePremiumSelection(insurer, period);
      }
    }
  }, [selectedPremium, handlePremiumSelection]);

  // Only show "No Quotes Available" if we have neither quotes nor requested insurers
  if (
    (!groupedQuotes || groupedQuotes.length === 0) &&
    (!requestedInsurers || requestedInsurers.length === 0)
  ) {
    return (
      <NoQuotesMessage>
        <InfoRegular />
        <h3>{t("qtg.mtpl.noQuotes.title", "No Quotes Available")}</h3>
        <p>
          {t(
            "qtg.mtpl.noQuotes.description",
            "Select insurers and click 'Get Quotes' to see results",
          )}
        </p>
      </NoQuotesMessage>
    );
  }

  const periods = getAllPeriods();

  return (
    <Container>
      <TableContainer>
        <Table>
          <TableHeader>
            <tr>
              <th></th>
              {allInsurersToDisplay.map(({ name, quote, savedQuote }) => {
                const insurerKey = name.toUpperCase();
                const isExcluded = isInsurerExcluded(insurerKey);

                return (
                  <ExcludedHeader key={name} excluded={isExcluded}>
                    {canEdit && onExclusionChange && (
                      <InsurerCheckboxWrapper>
                        <Checkbox
                          checked={!isExcluded}
                          onChange={() =>
                            handleInsurerExclusionToggle(insurerKey)
                          }
                          title={
                            isExcluded
                              ? t(
                                  "qtg.mtpl.table.includeInsurer",
                                  "Include insurer",
                                )
                              : t(
                                  "qtg.mtpl.table.excludeInsurer",
                                  "Exclude insurer",
                                )
                          }
                        />
                      </InsurerCheckboxWrapper>
                    )}
                    <InsurerHeader>
                      <StatusIcons>
                        <Tooltip
                          content={t(
                            "qtg.mtpl.table.createOfferStatus",
                            "Create Offer Status",
                          )}
                          relationship="label"
                        >
                          <StatusIconWrapper>
                            {getCreateOfferStatusIcon(quote)}
                          </StatusIconWrapper>
                        </Tooltip>
                        <Tooltip
                          content={t(
                            "qtg.mtpl.table.saveOfferStatus",
                            "Save Offer Status",
                          )}
                          relationship="label"
                        >
                          <StatusIconWrapper>
                            {getSaveOfferStatusIcon(savedQuote)}
                          </StatusIconWrapper>
                        </Tooltip>
                      </StatusIcons>
                      <InsurerName>{name}</InsurerName>
                      {quote?.HasPrices &&
                        quote?.Premiums &&
                        quote.Premiums.length > 0 && (
                          <Badge
                            appearance="outline"
                            color={quote.HasErrors ? "danger" : "success"}
                            size="small"
                            style={{ fontSize: "9px", padding: "0 4px" }}
                          >
                            {quote.Premiums.length} period
                            {quote.Premiums.length > 1 ? "s" : ""}
                          </Badge>
                        )}
                      {!quote && !savedQuote && (
                        <Badge
                          appearance="outline"
                          color="informative"
                          size="small"
                          style={{ fontSize: "9px", padding: "0 4px" }}
                        >
                          {t("qtg.mtpl.table.noData", "No data")}
                        </Badge>
                      )}
                    </InsurerHeader>
                  </ExcludedHeader>
                );
              })}
            </tr>
          </TableHeader>

          <TableBody>
            {/* Premiums Section - Merged (Saved + Regular) */}
            {periods.length > 0 && (
              <>
                <TableRow isSection>
                  <td colSpan={allInsurersToDisplay.length + 1}>
                    {t("qtg.mtpl.table.premiums", "Premiums by Period")}
                  </td>
                </TableRow>

                {periods.map((period) => (
                  <TableRow key={period}>
                    <td>
                      <RowLabel>
                        {period === 12
                          ? t("qtg.mtpl.table.period12", "12 months (1 year)")
                          : `${period} ${t("qtg.mtpl.table.months", "months")}`}
                      </RowLabel>
                    </td>
                    {allInsurersToDisplay.map(({ name, quote, savedQuote }) => {
                      const premiumData = quote
                        ? getPremiumDataForPeriod(quote, period)
                        : null;
                      const premiumKey = `${name}-${period}`;
                      const isSelected = selectedPremium === premiumKey;
                      const insurerKey = name.toUpperCase();
                      const isExcluded = isInsurerExcluded(insurerKey);

                      return (
                        <ExcludedCell
                          as="td"
                          key={name}
                          excluded={isExcluded}
                          className={isSelected ? "selected-premium" : ""}
                        >
                          {premiumData !== null &&
                          premiumData.premium !== null ? (
                            <PremiumCell>
                              {canEdit && onQuoteUpdate && !isExcluded && (
                                <EditIconButton
                                  appearance="subtle"
                                  size="small"
                                  icon={<EditRegular />}
                                  onClick={() =>
                                    handleEditClick(name, period, premiumData)
                                  }
                                  className={`edit-icon-button ${
                                    premiumData.isUpdated
                                      ? "always-visible"
                                      : ""
                                  }`}
                                  title={t(
                                    "qtg.mtpl.table.editPremium",
                                    "Edit premium value",
                                  )}
                                />
                              )}
                              <RadioLabel>
                                <input
                                  type="radio"
                                  name="premiumSelection"
                                  value={premiumKey}
                                  checked={isSelected}
                                  disabled={
                                    isExcluded ||
                                    (invoiceData !== null &&
                                      !isInvoicedOption(name, period)) ||
                                    premiumData.premium === null
                                  }
                                  onChange={() =>
                                    handlePremiumSelection(name, period)
                                  }
                                />
                                {premiumData.isUpdated ? (
                                  <Tooltip
                                    content={`${t(
                                      "qtg.mtpl.table.originalValue",
                                      "Original",
                                    )}: €${formatCurrency(
                                      premiumData.originalPremium ?? 0,
                                    )}`}
                                    relationship="description"
                                  >
                                    <UpdatedValue>
                                      €{formatCurrency(premiumData.premium)}
                                    </UpdatedValue>
                                  </Tooltip>
                                ) : (
                                  <PremiumValue>
                                    €{formatCurrency(premiumData.premium)}
                                  </PremiumValue>
                                )}
                              </RadioLabel>
                              {premiumData.policyNumber && (
                                <PolicyNumberInput
                                  type="text"
                                  value={premiumData.policyNumber}
                                  readOnly
                                  title={premiumData.policyNumber}
                                />
                              )}
                            </PremiumCell>
                          ) : (
                            <PremiumCell>
                              {canEdit &&
                                onQuoteUpdate &&
                                quote &&
                                !isExcluded && (
                                  <EditIconButton
                                    appearance="subtle"
                                    size="small"
                                    icon={<EditRegular />}
                                    onClick={() =>
                                      handleEditClick(
                                        name,
                                        period,
                                        premiumData || {
                                          premium: null,
                                          originalPremium: null,
                                          isSaved: false,
                                          isUpdated: false,
                                        },
                                      )
                                    }
                                    className={`edit-icon-button ${
                                      premiumData?.isUpdated
                                        ? "always-visible"
                                        : ""
                                    }`}
                                    title={
                                      premiumData?.isUpdated
                                        ? t(
                                            "qtg.mtpl.table.editPremium",
                                            "Edit premium value",
                                          )
                                        : t(
                                            "qtg.mtpl.table.addPremium",
                                            "Add premium value",
                                          )
                                    }
                                  />
                                )}
                              {premiumData?.isUpdated ? (
                                <Tooltip
                                  content={`${t(
                                    "qtg.mtpl.table.originalValue",
                                    "Original",
                                  )}: €${formatCurrency(
                                    premiumData.originalPremium ?? 0,
                                  )}`}
                                  relationship="description"
                                >
                                  <UpdatedValue>—</UpdatedValue>
                                </Tooltip>
                              ) : (
                                <NoDataCell>—</NoDataCell>
                              )}
                            </PremiumCell>
                          )}
                        </ExcludedCell>
                      );
                    })}
                  </TableRow>
                ))}
              </>
            )}

            {/* Warnings */}
            {allInsurersToDisplay.some(
              ({ quote }) => quote?.HasInsurerWarnings,
            ) && (
              <TableRow>
                <td>
                  <RowLabel>
                    <WarningRegular
                      style={{ fontSize: 12, color: "#faad14" }}
                    />
                    {t("qtg.mtpl.table.warnings", "Warnings")}
                  </RowLabel>
                </td>
                {allInsurersToDisplay.map(({ name, quote }) => {
                  // Get warnings (from Response.Mtpl only - no save phase for warnings)
                  const warnings: { message: string }[] = [];
                  if (quote?.HasInsurerWarnings && quote.ErrorList) {
                    const uniqueWarnings = getUniqueErrors(quote.ErrorList);
                    uniqueWarnings.forEach((e) => {
                      warnings.push({ message: e.Message });
                    });
                  }
                  const insurerKey = name.toUpperCase();
                  const isExcluded = isInsurerExcluded(insurerKey);
                  return (
                    <ExcludedCell as="td" key={name} excluded={isExcluded}>
                      {warnings.length > 0 ? (
                        <Tooltip
                          content={
                            <div style={{ whiteSpace: "pre-line" }}>
                              {warnings.map((w, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    marginBottom:
                                      idx < warnings.length - 1 ? 4 : 0,
                                  }}
                                >
                                  {w.message}
                                </div>
                              ))}
                            </div>
                          }
                          relationship="description"
                        >
                          <WarningMessage>
                            {warnings.length} warning
                            {warnings.length > 1 ? "s" : ""}
                          </WarningMessage>
                        </Tooltip>
                      ) : (
                        <NoDataCell>—</NoDataCell>
                      )}
                    </ExcludedCell>
                  );
                })}
              </TableRow>
            )}

            {/* Errors */}
            {allInsurersToDisplay.some(
              ({ quote, savedQuote }) =>
                quote?.HasErrors ||
                quote?.HasInsurerErrors ||
                savedQuote?.HasErrors ||
                savedQuote?.HasInsurerErrors,
            ) && (
              <TableRow hasError>
                <td>
                  <RowLabel>
                    <ErrorCircleRegular
                      style={{ fontSize: 12, color: "#ff4d4f" }}
                    />
                    {t("qtg.mtpl.table.errors", "Errors")}
                  </RowLabel>
                </td>
                {allInsurersToDisplay.map(({ name, quote, savedQuote }) => {
                  // Get create/quote errors (from Response.Mtpl)
                  const createErrors: { message: string; source: string }[] =
                    [];
                  if (
                    quote &&
                    (quote.HasErrors ||
                      quote.HasInsurerErrors ||
                      quote.HasInsurerWarnings) &&
                    quote.ErrorList
                  ) {
                    const uniqueErrors = getUniqueErrors(quote.ErrorList);
                    uniqueErrors.forEach((e) => {
                      createErrors.push({
                        message: e.Message,
                        source: "Create",
                      });
                    });
                  }

                  // Get ALL saved errors for this insurer (from Response.MtplSaved - all periods)
                  const insurerKey = name.toLowerCase();
                  const allSavedForInsurer =
                    allSavedPoliciesByInsurer[insurerKey] || [];
                  const allSaveErrors: { message: string; source: string }[] =
                    [];

                  allSavedForInsurer.forEach((saved) => {
                    if (
                      (saved.HasErrors ||
                        saved.HasInsurerErrors ||
                        saved.HasInsurerWarnings) &&
                      saved.ErrorList
                    ) {
                      const uniqueErrors = getUniqueErrors(saved.ErrorList);
                      uniqueErrors.forEach((e) => {
                        // Add unique errors only
                        if (
                          !allSaveErrors.some(
                            (err) => err.message === e.Message,
                          )
                        ) {
                          allSaveErrors.push({
                            message: e.Message,
                            source: "Save",
                          });
                        }
                      });
                    }
                  });

                  const totalCreateErrors = createErrors.length;
                  const totalSaveErrors = allSaveErrors.length;
                  const allErrors = [...createErrors, ...allSaveErrors];
                  const isExcluded = isInsurerExcluded(
                    insurerKey.toUpperCase(),
                  );

                  return (
                    <ExcludedCell as="td" key={name} excluded={isExcluded}>
                      {totalCreateErrors > 0 || totalSaveErrors > 0 ? (
                        <Tooltip
                          content={
                            <div style={{ whiteSpace: "pre-line" }}>
                              {allErrors.map((e, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    marginBottom:
                                      idx < allErrors.length - 1 ? 4 : 0,
                                  }}
                                >
                                  [{e.source}] {e.message}
                                </div>
                              ))}
                            </div>
                          }
                          relationship="description"
                        >
                          <ErrorMessage>
                            {`${totalCreateErrors}+${totalSaveErrors} errors`}
                          </ErrorMessage>
                        </Tooltip>
                      ) : (
                        <NoDataCell>—</NoDataCell>
                      )}
                    </ExcludedCell>
                  );
                })}
              </TableRow>
            )}

            {/* Execution Time */}
            <TableRow>
              <td>
                <RowLabel>
                  {t("qtg.mtpl.table.executionTime", "Execution Time")}
                </RowLabel>
              </td>
              {allInsurersToDisplay.map(({ name, quote, savedQuote }) => {
                const insurerKey = name.toUpperCase();
                const isExcluded = isInsurerExcluded(insurerKey);
                return (
                  <ExcludedCell as="td" key={name} excluded={isExcluded}>
                    {quote ? (
                      formatDuration(quote.Duration)
                    ) : (
                      <NoDataCell>—</NoDataCell>
                    )}
                  </ExcludedCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Premium Dialog */}
      {editingQuote && (
        <EditPremiumDialog
          open={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setEditingQuote(null);
          }}
          onSave={handleSaveEdit}
          quoteData={editingQuote}
          insuranceType="mtpl"
        />
      )}
    </Container>
  );
};

export default MtplQuotesDisplay;
